import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { ArrowRight, Eye, FolderKanban, RefreshCw, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { supabase } from '../lib/supabaseClient';
import { fetchProfileLookups, formatProdi, hydrateProfile } from '../lib/profileLookups';
import { formatRoleLabel, getCriteriaScope, isSuperadmin } from '../lib/accessRules';
import { exportCriteriaArchiveToDocx } from '../lib/exportHelpers';
import { fetchKriteriaList, mapCriteriaForAccess } from '../lib/kriteriaMetadata';

function ArsipGlobal() {
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [archiveRows, setArchiveRows] = useState([]);
  const [criteriaConfig, setCriteriaConfig] = useState([]);
  const [prodiList, setProdiList] = useState([]);
  const [selectedProdiId, setSelectedProdiId] = useState('');
  const [selectedProdiText, setSelectedProdiText] = useState('');
  const [selectedCriteria, setSelectedCriteria] = useState('Semua');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  const getVisibleCriteria = useCallback((profileData, config = []) => {
    return getCriteriaScope(profileData, config);
  }, []);

  const formatDateTime = (value) => {
    if (!value) return '-';

    return new Date(value).toLocaleString('id-ID', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const shortenText = (value, maxLength = 95) => {
    const text = String(value || '-');

    if (text.length <= maxLength) return text;

    return `${text.slice(0, maxLength)}...`;
  };

  const getProfileMap = async (rows) => {
    const profileIds = [
      ...new Set(rows.map((row) => row.created_by).filter(Boolean)),
    ];

    if (profileIds.length === 0) return new Map();

    const { data, error } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .select('id, nama_lengkap')
      .in('id', profileIds);

    if (error) {
      throw new Error(`Gagal mengambil data penyimpan: ${error.message}`);
    }

    return new Map((data || []).map((item) => [item.id, item.nama_lengkap]));
  };

  const loadArchiveData = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      navigate('/');
      return;
    }

    const { data: rawProfileData, error: profileError } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (profileError || !rawProfileData) {
      setErrorMsg('Profile user tidak ditemukan.');
      setLoading(false);
      return;
    }

    if (!rawProfileData.is_active) {
      setErrorMsg('Akun nonaktif.');
      setLoading(false);
      return;
    }

    let profileData = rawProfileData;
    let scopedProdiId = null;

    try {
      const lookups = await fetchProfileLookups(supabase);
      const hydratedProfile = hydrateProfile(profileData, lookups);
      setProfile(hydratedProfile);
      setProdiList(lookups.prodiList || []);
      profileData = hydratedProfile;

      const selectedProdi = isSuperadmin(profileData)
        ? (lookups.prodiList || []).find((item) => item.id === selectedProdiId) ||
          (lookups.prodiList || [])[0] ||
          null
        : profileData.prodiData || null;

      if (isSuperadmin(profileData) && selectedProdi?.id && !selectedProdiId) {
        setSelectedProdiId(selectedProdi.id);
      }

      setSelectedProdiText(selectedProdi ? formatProdi(selectedProdi) : '');

      scopedProdiId = selectedProdi?.id || null;
    } catch (lookupError) {
      setErrorMsg(lookupError.message);
      setLoading(false);
      return;
    }

    try {
      const dynamicCriteria = await fetchKriteriaList(supabase, {
        prodiId: scopedProdiId,
      });
      const nextCriteriaConfig = mapCriteriaForAccess(dynamicCriteria).map((criteria) => ({
        code: criteria.kode_kriteria,
        title: criteria.nama_kriteria,
        route: isSuperadmin(profileData) && scopedProdiId
          ? `${criteria.route}?prodi=${scopedProdiId}`
          : criteria.route,
        summaryField: criteria.fields?.[0]?.nama_field || 'data',
        fields: (criteria.fields || []).map((field) => ({
          name: field.nama_field,
          label: field.label,
        })),
        criteriaType: criteria.tipe_kriteria,
        criteriaId: criteria.id,
      }));
      setCriteriaConfig(nextCriteriaConfig);

      const visibleCriteria = getVisibleCriteria(profileData, nextCriteriaConfig);
      setSelectedCriteria(
        (prev) =>
          prev === 'Semua' || visibleCriteria.some((config) => config.code === prev)
            ? prev
            : isSuperadmin(profileData)
              ? 'Semua'
              : visibleCriteria[0]?.code || 'Semua'
      );

      const results = await Promise.all(
        visibleCriteria.map(async (config) => {
          let query = supabase
            .schema('akreditasi')
            .from('kriteria_data')
            .select(`
              *,
              prodi:prodi_id (
                id,
                nama_prodi,
                jenjang
              )
            `)
            .eq('kriteria_id', config.criteriaId)
            .eq('is_deleted', false);

          if (scopedProdiId) {
            query = query.eq('prodi_id', scopedProdiId);
          }

          const { data, error } = await query;

          if (error) {
            throw new Error(`Gagal mengambil data ${config.code}: ${error.message}`);
          }

          return (data || []).map((item) => ({
            archiveKey: `${config.code}-${item.id}`,
            criteriaCode: config.code,
            criteriaTitle: config.title,
            sourceRoute: config.route,
            summaryField: config.summaryField,
            fields: config.fields,
            criteriaType: config.criteriaType,
            rawData: { ...(item.data_json || {}), ...item },
            prodiText: item.prodi
              ? `${item.prodi.jenjang} - ${item.prodi.nama_prodi}`
              : '-',
            summaryText: item.data_json?.[config.summaryField] || '-',
            createdBy: item.created_by || null,
            createdAt: item.created_at || null,
          }));
        })
      );

      const mergedRows = results.flat();
      const profileMap = await getProfileMap(mergedRows.map((row) => row.rawData));

      const rowsWithProfile = mergedRows
        .map((row) => ({
          ...row,
          createdByName: row.createdBy
            ? profileMap.get(row.createdBy) || 'Profile tidak ditemukan'
            : '-',
        }))
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

      setArchiveRows(rowsWithProfile);
    } catch (error) {
      setErrorMsg(error.message);
    }

    setLoading(false);
  }, [getVisibleCriteria, navigate, selectedProdiId]);

  useEffect(() => {
    loadArchiveData();
  }, [loadArchiveData]);

  const filteredRows = useMemo(() => {
    const criteriaRows =
      selectedCriteria === 'Semua'
        ? archiveRows
        : archiveRows.filter((row) => row.criteriaCode === selectedCriteria);

    if (!searchTerm.trim()) return criteriaRows;

    const keyword = searchTerm.toLowerCase();

    return criteriaRows.filter((row) => {
      const searchableText = [
        row.criteriaCode,
        row.criteriaTitle,
        row.prodiText,
        row.summaryText,
        row.createdByName,
        formatDateTime(row.createdAt),
      ]
        .join(' ')
        .toLowerCase();

      return searchableText.includes(keyword);
    });
  }, [archiveRows, selectedCriteria, searchTerm]);

  const totalByCriteria = useMemo(
    () =>
      getVisibleCriteria(profile || {}, criteriaConfig).map((config) => ({
        ...config,
        total: archiveRows.filter((row) => row.criteriaCode === config.code).length,
      })),
    [archiveRows, criteriaConfig, getVisibleCriteria, profile]
  );

  const visibleCriteriaOptions = useMemo(
    () => getVisibleCriteria(profile || {}, criteriaConfig),
    [criteriaConfig, getVisibleCriteria, profile]
  );

  const userIsSuperadmin = isSuperadmin(profile);

  const handleExportAllDocx = () => {
    exportCriteriaArchiveToDocx({
      fileName: userIsSuperadmin
        ? `dokumen-akreditasi-${selectedProdiText || 'prodi'}.docx`
        : `dokumen-akreditasi-${profile?.prodi || formatRoleLabel(profile).toLowerCase().replace(/\s+/g, '-') || 'prodi'}.docx`,
      title: 'Dokumen Akreditasi',
      subtitle: userIsSuperadmin
        ? selectedProdiText || 'Program Studi'
        : profile?.prodi_display || formatRoleLabel(profile) || 'Scope Prodi',
      rows: filteredRows,
    });
  };

  if (loading) {
    return (
      <DashboardLayout
        title="Arsip Data Akreditasi"
        breadcrumbs={['Dashboard', 'Rekap & Arsip', 'Arsip Data']}
        profile={profile}
        activeMenu="arsip"
      >
        <div className="page-loading-v2">
          <Spinner animation="border" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Arsip Data Akreditasi"
      breadcrumbs={['Dashboard', 'Rekap & Arsip', 'Arsip Data']}
      profile={profile}
      activeMenu="arsip"
    >
      <Container fluid className="p-0">
        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

        <div className="archive-toolbar mb-4">
          <div>
            <h2 className="archive-page-title">Arsip Data Akreditasi</h2>
          </div>

          <div className="archive-toolbar-actions">
            <Button className="archive-btn archive-btn-green" onClick={handleExportAllDocx}>
              Download Word
            </Button>
            <Button className="archive-btn archive-btn-blue" onClick={loadArchiveData}>
              <RefreshCw size={16} className="me-2" />
              Muat Ulang
            </Button>
          </div>
        </div>

        <Row className="g-4 mb-4">
          <Col xl={9}>
            <Card className="archive-main-card">
              <Card.Body>
                <div className="section-head-v2 mb-3">
                  <div>
                    <h4 className="section-title-v2">Data Gabungan Kriteria</h4>
                  </div>

                  <div className="d-flex gap-2 flex-wrap justify-content-end">
                    {userIsSuperadmin && (
                      <Form.Select
                        style={{ maxWidth: 260 }}
                        value={selectedProdiId}
                        onChange={(e) => setSelectedProdiId(e.target.value)}
                      >
                        {prodiList.map((prodi) => (
                          <option key={prodi.id} value={prodi.id}>
                            {formatProdi(prodi)}
                          </option>
                        ))}
                      </Form.Select>
                    )}

                    <InputGroup className="criterion-search-box">
                      <InputGroup.Text>
                        <Search size={16} />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Cari arsip..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>

                    {userIsSuperadmin ? (
                      <Form.Select
                        style={{ maxWidth: 220 }}
                        value={selectedCriteria}
                        onChange={(e) => setSelectedCriteria(e.target.value)}
                      >
                        <option value="Semua">Semua Kriteria</option>
                        {visibleCriteriaOptions.map((config) => (
                          <option key={config.code} value={config.code}>
                            {config.code}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <div className="archive-scope-badge">
                        {visibleCriteriaOptions[0]?.code
                          ? `TEAM ${visibleCriteriaOptions[0].code}`
                          : formatRoleLabel(profile)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="archive-table-wrap">
                  <Table responsive hover className="criterion-table align-middle">
                    <thead>
                      <tr>
                        <th>No</th>
                        <th>Kriteria</th>
                        <th>Prodi</th>
                        <th>Ringkasan Data</th>
                        <th>Disimpan Oleh</th>
                        <th>Tanggal Simpan</th>
                        <th>Status</th>
                        <th>Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.length > 0 ? (
                        filteredRows.map((item, index) => (
                          <tr key={item.archiveKey}>
                            <td>{index + 1}</td>
                            <td>
                              <Badge bg="primary">{item.criteriaCode}</Badge>
                              <div className="small text-muted mt-1">
                                {item.criteriaTitle}
                              </div>
                            </td>
                            <td>{item.prodiText}</td>
                            <td>{shortenText(item.summaryText)}</td>
                            <td>{item.createdByName}</td>
                            <td>{formatDateTime(item.createdAt)}</td>
                            <td>
                              <Badge bg="success">Aktif</Badge>
                            </td>
                            <td>
                              <div className="table-action-group">
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  title="Lihat detail"
                                  onClick={() => setSelectedDetail(item)}
                                >
                                  <Eye size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  title="Buka halaman asal"
                                  onClick={() => navigate(item.sourceRoute)}
                                >
                                  <ArrowRight size={14} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center py-4">
                            Belum ada data arsip untuk filter ini.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={3}>
            <Card className="archive-side-card success">
              <Card.Body>
                <div className="archive-side-head">
                  <FolderKanban size={28} />
                  <div>
                    <div className="archive-side-title">Total Arsip Aktif</div>
                    <div className="archive-side-text">
                      {archiveRows.length} data dari {userIsSuperadmin ? selectedProdiText || 'prodi terpilih' : profile?.prodi_display || 'prodi Anda'}.
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className="archive-side-card mt-4">
              <Card.Body>
                <div className="archive-side-title mb-3">Ringkasan Kriteria</div>
                <div className="archive-side-meta">
                  {totalByCriteria.map((item) => (
                    <div key={item.code} className="d-flex justify-content-between gap-3">
                      <strong>{item.code}</strong>
                      <span>{item.total} data</span>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Modal
          show={!!selectedDetail}
          onHide={() => setSelectedDetail(null)}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Detail Arsip {selectedDetail?.criteriaCode || ''}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {selectedDetail && (
              <>
                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <div className="info-box-v2">
                      <div className="info-label-v2">Kriteria</div>
                      <div className="info-value-v2">
                        {selectedDetail.criteriaCode} - {selectedDetail.criteriaTitle}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="info-box-v2">
                      <div className="info-label-v2">Program Studi</div>
                      <div className="info-value-v2">{selectedDetail.prodiText}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="info-box-v2">
                      <div className="info-label-v2">Disimpan Oleh</div>
                      <div className="info-value-v2">{selectedDetail.createdByName}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="info-box-v2">
                      <div className="info-label-v2">Tanggal Simpan</div>
                      <div className="info-value-v2">
                        {formatDateTime(selectedDetail.createdAt)}
                      </div>
                    </div>
                  </Col>
                </Row>

                <Table responsive bordered className="criterion-table mb-0">
                  <tbody>
                    {selectedDetail.fields.map((field) => (
                      <tr key={field.name}>
                        <th style={{ width: '34%' }}>{field.label}</th>
                      <td>{String(selectedDetail.rawData[field.name] || '-')}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setSelectedDetail(null)}>
              Tutup
            </Button>
            {selectedDetail && (
              <Button
                className="save-btn-v2"
                onClick={() => navigate(selectedDetail.sourceRoute)}
              >
                Buka Halaman Asal
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </Container>
    </DashboardLayout>
  );
}

export default ArsipGlobal;
